import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { listCache } from '../utils/listCache';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  MdAdd, MdLocationOn, MdPerson, MdPhotoLibrary,
  MdCalendarToday, MdGroups, MdAssignment,
} from 'react-icons/md';

const AddIcon       = MdAdd as any;
const LocationIcon  = MdLocationOn as any;
const PersonIcon    = MdPerson as any;
const PhotoIcon     = MdPhotoLibrary as any;
const CalendarIcon  = MdCalendarToday as any;
const GroupsIcon    = MdGroups as any;
const SurveyIcon    = MdAssignment as any;

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

export const SurveyActivity: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const ts = (t as any).surveyActivity || {};

  const initialCache = listCache.restore('survey-activities');
  const [activities, setActivities] = useState<any[]>(initialCache?.items ?? []);
  const [loading, setLoading] = useState(!initialCache);
  const [offset, setOffset] = useState<number>(initialCache?.cursor ?? 0);
  const [hasMore, setHasMore] = useState<boolean>(initialCache?.hasMore ?? true);
  const isFirstOffsetRun = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const scrollTopRef = useRef<number>(initialCache?.scrollTop ?? 0);

  useEffect(() => {
    setTimeout(() => { window.scroll({ top: -1, left: 0, behavior: 'smooth' }); }, 10);
  }, [pathname]);

  useEffect(() => {
    if (initialCache) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById('scrollableDiv');
          if (el) el.scrollTop = scrollTopRef.current;
        });
      });
      listCache.clear('survey-activities');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActivities = useCallback(async (currentOffset: number) => {
    if (isLoadingMoreRef.current && currentOffset > 0) return;
    try {
      if (currentOffset === 0) setLoading(true);
      else isLoadingMoreRef.current = true;
      const res = await axiosInstance.get(`/survey-activities?limit=10&offset=${currentOffset}`);
      const newData: any[] = res.data;
      setHasMore(newData.length >= 10);
      if (currentOffset === 0) {
        setActivities(newData);
      } else {
        setActivities(prev => {
          const ids = new Set(prev.map(a => a.id));
          return [...prev, ...newData.filter(a => !ids.has(a.id))];
        });
      }
    } catch (err) {
      console.error('Failed to fetch survey activities:', err);
      if (currentOffset === 0) setActivities([]);
    } finally {
      setLoading(false);
      isLoadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isFirstOffsetRun.current) { isFirstOffsetRun.current = false; return; }
    if (offset > 0) fetchActivities(offset);
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialCache) fetchActivities(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr: string) =>
    new Date(dateStr.slice(0, 10) + 'T00:00:00').toLocaleDateString(
      language === 'id' ? 'id-ID' : 'en-US',
      { day: 'numeric', month: 'short', year: 'numeric' }
    );

  const getStatusBadge = (status: string) => {
    if (status === 'submitted') return { label: ts.statusSubmitted || 'Submitted', bg: '#D1FAE5', color: '#065F46' };
    if (status === 'active') return { label: ts.statusActive || 'Link Shared', bg: '#DBEAFE', color: '#1E40AF' };
    return { label: ts.statusDraft || 'Draft', bg: '#F3F4F6', color: '#6B7280' };
  };

  const handleCardClick = (act: any) => {
    listCache.save('survey-activities', { items: activities, cursor: offset, hasMore, scrollTop: scrollTopRef.current });
    navigate('/DetailSurveyActivity', { state: { activityId: act.id } });
  };

  const renderCard = (act: any) => {
    const images: string[] = act.images || [];
    const thumbUrl = images.length > 0 ? `${IMAGE_BASE_URL}${images[0]}` : null;

    return (
      <div
        key={act.id}
        onClick={() => handleCardClick(act)}
        style={{
          backgroundColor: '#fff',
          borderRadius: 14,
          border: '1px solid #E5E7EB',
          borderLeft: '4px solid #3B82F6',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          marginBottom: 12,
          overflow: 'hidden',
        }}
      >
        {/* Top row: location name + date */}
        <div style={{ padding: '14px 14px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <text.H5 style={{
            color: theme.colors.mainDark, fontSize: 15, fontWeight: '700',
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize'
          }}>
            {act.location_name || ts.noLocationName || 'Survey Location'}
          </text.H5>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            {(() => {
              const badge = getStatusBadge(act.status);
              return (
                <div style={{ backgroundColor: badge.bg, borderRadius: 6, padding: '2px 7px' }}>
                  <text.T14 style={{ fontSize: 10, color: badge.color, fontWeight: '700', whiteSpace: 'nowrap' }}>
                    {badge.label}
                  </text.T14>
                </div>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#EFF6FF', borderRadius: 6, padding: '2px 7px' }}>
              <CalendarIcon size={11} color="#3B82F6" />
              <text.T14 style={{ fontSize: 11, color: '#3B82F6', fontWeight: '600', whiteSpace: 'nowrap' }}>
                {formatDate(act.survey_date)}
              </text.T14>
            </div>
          </div>
        </div>

        {/* Findings preview */}
        <div style={{ paddingLeft: 14, paddingRight: 14, paddingBottom: 10 }}>
          {act.findings ? (
            <text.T14 style={{
              color: '#4B5563', lineHeight: 1.55, fontSize: 13,
              display: '-webkit-box' as any,
              overflow: 'hidden', textOverflow: 'ellipsis',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
              textTransform: 'capitalize'
            }}>
              {act.findings}
            </text.T14>
          ) : (
            <text.T14 style={{ color: '#D1D5DB', fontStyle: 'italic', fontSize: 13, textTransform: 'capitalize' }}>
              {ts.noFindings || 'No findings recorded'}
            </text.T14>
          )}
        </div>

        {/* Bottom row: stats + thumb */}
        <div style={{ padding: '8px 14px 12px', display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px dashed #F3F4F6' }}>
          {/* Stat chips */}
          <div style={{ display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap' }}>
            {act.respondent_count > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#EFF6FF', borderRadius: 20, padding: '3px 8px' }}>
                <GroupsIcon size={12} color="#3B82F6" />
                <text.T14 style={{ fontSize: 11, color: '#3B82F6', fontWeight: '700' }}>
                  {act.respondent_count} {ts.respondents || 'resp.'}
                </text.T14>
              </div>
            )}
            {images.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#F5F3FF', borderRadius: 20, padding: '3px 8px' }}>
                <PhotoIcon size={12} color="#8B5CF6" />
                <text.T14 style={{ fontSize: 11, color: '#8B5CF6', fontWeight: '700' }}>
                  {images.length} {ts.photos || 'foto'}
                </text.T14>
              </div>
            )}
            {act.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, maxWidth: 160 }}>
                <LocationIcon size={12} color="#9CA3AF" />
                <text.T14 style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {act.address}
                </text.T14>
              </div>
            )}
          </div>

          {/* Thumbnail + user */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            {thumbUrl && (
              <div style={{ width: 44, height: 44, borderRadius: 8, overflow: 'hidden', border: '2px solid #E5E7EB' }}>
                <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            {act.user_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <PersonIcon size={11} color="#9CA3AF" />
                <text.T14 style={{ fontSize: 10, color: '#9CA3AF', textTransform: 'capitalize' }}>
                  {act.user_name}
                </text.T14>
              </div>
            )}
          </div>
        </div>

      </div>
    );
  };

  const renderHeader = () => (
    <components.Header
      title={ts.title || 'Survey Activities'}
      goBack={true}
      plus={true}
      onPlus={() => navigate('/CreateSurveyActivity')}
    />
  );

  const renderContent = () => (
    <main
      id="scrollableDiv"
      className="container"
      onScroll={(e) => { scrollTopRef.current = (e.currentTarget as HTMLElement).scrollTop; }}
      style={{ marginTop: 60, paddingBottom: 100, height: 'calc(100vh - 60px)', overflowY: 'auto' }}
    >
      {/* Header stats strip */}
      {!loading && activities.length > 0 && (
        <div style={{
          display: 'flex', gap: 12, marginBottom: 20, marginTop: 16,
          padding: '14px 16px',
          background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
          borderRadius: 14,
          color: 'white',
          alignItems: 'center',
        }}>
          <div style={{ flex: 1 }}>
            <text.T14 style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
              {ts.title || 'Survey Activities'}
            </text.T14>
            <text.H4 style={{ color: 'white', marginTop: 2 }}>
              {activities.length}{hasMore ? '+' : ''} Survey{activities.length !== 1 ? 's' : ''}
            </text.H4>
          </div>
          <div
            onClick={() => navigate('/CreateSurveyActivity')}
            style={{
              width: 42, height: 42, borderRadius: 12,
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', border: '1px solid rgba(255,255,255,0.3)'
            }}
          >
            <AddIcon size={22} color="white" />
          </div>
        </div>
      )}

      {loading ? (
        <text.T16 style={{ textAlign: 'center', marginTop: 60, color: '#9CA3AF' }}>
          {ts.loading || 'Loading surveys...'}
        </text.T16>
      ) : activities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 24,
            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <SurveyIcon size={38} color="#3B82F6" />
          </div>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {ts.noActivities || 'No Surveys Yet'}
          </text.H5>
          <text.T14 style={{ color: '#9CA3AF', marginBottom: 28, lineHeight: 1.5 }}>
            {ts.noActivitiesDesc || "You haven't recorded any survey activities yet."}
          </text.T14>
          <div
            onClick={() => navigate('/CreateSurveyActivity')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              color: 'white', padding: '12px 24px', borderRadius: 12,
              cursor: 'pointer', fontWeight: '600', fontSize: 14,
            }}
          >
            <AddIcon size={18} color="white" />
            {ts.create || 'Record Survey'}
          </div>
        </div>
      ) : (
        <div style={{ paddingTop: 4 }}>
          <InfiniteScroll
            dataLength={activities.length}
            next={() => setOffset(prev => prev + 10)}
            hasMore={hasMore}
            loader={
              <div style={{ padding: '10px 0' }}>
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center' }}>
                  {ts.loadingMore || 'Loading more...'}
                </text.T14>
              </div>
            }
            endMessage={
              <div style={{ padding: '10px 0', marginBottom: 20 }}>
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center' }}>
                  {ts.allLoaded || 'All surveys loaded'}
                </text.T14>
              </div>
            }
            scrollableTarget="scrollableDiv"
            style={{ overflow: 'visible' }}
          >
            {activities.map(act => renderCard(act))}
          </InfiniteScroll>
        </div>
      )}
    </main>
  );

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <style>{`::-webkit-scrollbar { display: none; }`}</style>
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
